import { useCallback, useEffect, useReducer, useRef } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'

import { Button, DrawerBodyWrapper, Input, Select, SectionHeader, TOAST_TYPE } from '@base'
import { Spacings, Text } from '@styles'
import { CountryCodes, ORG_USER_ROLES, strings } from '@constants'
import { ActionButtonWapper, BodySectionWrapper, ButtonIcon, ButtonIconWapper, FooterWrapper } from './styles'
import RejectReason from './RejectReason'
import { Add, ChevronLeftOutlined, ChevronRightOutlined, Edit } from '@material-ui/icons'
import {
	addAddressToOrganisation,
	addUser,
	getAddressListFromBoth,
	getUserAddressesApi,
	getUsersOfOrganisation,
	updateOrganisationAddressApi,
} from '@data'
import { Address, User } from '@data/model'
import { showLoader, showToast } from '@data/state/action'
import BasicModal from '@base/basic/BasicModal'
import { ACTIONS, initialState, pocDetailsReducer } from './PocDetailsReducer'

const SectionItemWrapper = styled.div`
	display: flex;
	flex-shrink: unset;
	flex-basis: 100%;
	height: 100%;
	justify-content: ${({ justifyContent }) => justifyContent || 'flex-end'};
	flex-direction: ${({ direction }) => direction || 'column'};
	margin-top: ${({ marginTop }) => marginTop || `${Spacings.SPACING_2B}`};

	& > div {
		width: 100%;
	}
`

const SectionHeaderWrapper = styled.div`
	& > h4 {
		padding-bottom: ${Spacings.SPACING_1B};
		margin-bottom: ${Spacings.SPACING_1B};
	}
`

const Divider = styled.hr`
	border: none;
	border-top: 1px solid ${({ theme }) => theme.palette.background.greyBorder};
	margin: ${Spacings.SPACING_4B} 0 0;
`

const SectionHeaderWithActionWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-top: ${Spacings.SPACING_4B};

	& > h4 {
		padding-bottom: 0;
		margin-bottom: 0;
	}
`

const AddNewUserWrapper = styled.div`
	display: flex;
	justify-content: flex-end;
	margin-top: ${Spacings.SPACING_2B};
`

const ModalContentWrapper = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	justify-content: space-between;
`

const ModalFormWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_3B};
	flex: 1;
`

const ModalFooterWrapper = styled.div`
	display: flex;
	justify-content: flex-end;
	margin-top: ${Spacings.SPACING_4B};
`

const MobileFieldWrapper = styled.div`
	display: flex;
	align-items: flex-end;
	gap: ${Spacings.SPACING_2B};

	& > *:first-child {
		flex: 0 0 20%;
		min-width: 0;
	}

	& > *:last-child {
		flex: 1;
		min-width: 0;
	}
`

const AddressModalHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: ${Spacings.SPACING_3B};
`

const AddressListWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_2B};
	flex: 1;
	overflow-y: auto;
	max-height: 35vh;

	&::-webkit-scrollbar {
		display: none;
	}
	-ms-overflow-style: none;
	scrollbar-width: none;
`

const AddressCard = styled.div`
	padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_3B};
	border: 1.5px solid ${({ selected, theme }) => (selected ? theme.palette.background.blue : 'transparent')};
	border-radius: ${Spacings.SPACING_2B};
	cursor: ${({ readOnly }) => (readOnly ? 'default' : 'pointer')};
	background: ${({ theme }) => theme.palette.background.blueLightest};
	transition: border-color 0.15s;

	&:hover {
		border-color: ${({ readOnly, theme }) => (readOnly ? 'transparent' : theme.palette.background.blue)};
	}
`

const AddressCardLine = styled.span`
	display: inline-block;
	font-size: ${Text.MEDIUM};
	font-weight: 500;
	color: ${({ theme }) => theme.palette.text.darkblack};
	margin-bottom: ${({ marginBottom }) => (marginBottom ? Spacings.SPACING_1B : 0)};
`

const AddressCardItem = styled.span`
	font-size: ${Text.SMALL};
	font-weight: 600;
	color: ${({ theme }) => theme.palette.text.darkblack};
	text-transform: uppercase;
`

const AddressCardLabel = styled.div`
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_600};
	color: ${({ theme }) => theme.palette.text.label};
	display: inline-block;
	margin-bottom: ${Spacings.SPACING_2B};
`

const NoAddressText = styled.p`
	color: ${({ theme }) => theme.palette.text.greydark};
	font-size: 0.875rem;
	text-align: center;
	margin: ${Spacings.SPACING_4B} 0;
`

const AddressListFooterWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	margin-top: ${Spacings.SPACING_4B};
`

const AddressFormRevealWrapper = styled.div`
	opacity: ${({ show }) => (show ? 1 : 0)};
	visibility: ${({ show }) => (show ? 'visible' : 'hidden')};
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_3B};
	transition: opacity 0.2s ease-in-out;
`

const IconWrapper = styled(Button)`
	padding: ${Spacings.SPACING_1B} ${Spacings.SPACING_4B};
	min-width: unset;
	line-height: 0;
`

const SelectWithButtonWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
	margin-top: ${Spacings.SPACING_2B};

	& > div:first-child {
		flex: 1;
	}
`

const PocDetails = ({ nextStep, preStep, org, adminView, kycStatushandler, selectedStep, setOrgData }) => {
	const dispatch = useDispatch()
	const loggedInUser = useSelector((state) => state.getIn(['user', 'user']))
	const [state, uiDispatch] = useReducer(pocDetailsReducer, initialState)
	const {
		isActiveReason,
		userList,
		selectedUser,
		activeModal,
		addressList,
		selectedAddress,
		commAddressList,
		selectedCommAddress,
		addressModalView,
		addressSearchQuery,
		addressSearchResults,
		newAddress,
		newUser,
		headofficeAddress,
		communicationAddress,
		kycPocDetails,
	} = state
	const searchTimeoutId = useRef()

	const orgUserRoleOptions = Object.entries(ORG_USER_ROLES).map(([key, value]) => ({ key, label: value }))

	const closeModal = () => uiDispatch({ type: ACTIONS.CLOSE_MODAL })

	const updateNewUser = (field) => (value) => uiDispatch({ type: ACTIONS.UPDATE_NEW_USER, payload: { field, value } })

	const fetchOrgUsers = useCallback((orgId) => {
		dispatch(showLoader(true))
		getUsersOfOrganisation(orgId)
			.then((response) => {
				uiDispatch({
					type: ACTIONS.SET_USER_LIST,
					payload: response?.map((item) => ({ ...item, display_name: item?.full_name ?? item?.mobile_number })),
				})
			})
			.catch((error) => {
				const errorMsg = error?.response?.data?.message ?? strings('msg_failed_to_fetch')
				dispatch(showToast(true, errorMsg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => dispatch(showLoader(false)))
	}, [])

	const openModal = useCallback(
		(key) => {
			const orgId = org?.user_details?.organisation_id
			const isAddressModal = key === 'addAddress' || key === 'addCommunicationAddress'
			if (isAddressModal && orgId) {
				const setList = key === 'addAddress' ? ACTIONS.SET_ADDRESS_LIST : ACTIONS.SET_COMM_ADDRESS_LIST
				dispatch(showLoader(true))
				getUserAddressesApi(org?.user_details?.user_id)
					.then((response) => uiDispatch({ type: setList, payload: response ?? [] }))
					.catch(() => uiDispatch({ type: setList, payload: [] }))
					.finally(() => dispatch(showLoader(false)))
			}
			uiDispatch({ type: ACTIONS.OPEN_MODAL, payload: key })
		},
		[org?.user_details?.organisation_id]
	)

	useEffect(() => {
		const orgId = org?.user_details?.organisation_id
		if (!orgId) return
		fetchOrgUsers(orgId)
	}, [org?.user_details?.organisation_id, fetchOrgUsers])

	useEffect(() => {
		if (!userList?.length || !org?.kyc_poc_details?.user_id) return
		const preSelected = userList.find((item) => item.user_id === org.kyc_poc_details.user_id)
		if (preSelected) uiDispatch({ type: ACTIONS.SET_SELECTED_USER, payload: preSelected })
	}, [userList, org?.kyc_poc_details?.user_id])

	const submitNewUser = () => {
		const orgId = org?.user_details?.organisation_id
		dispatch(showLoader(true))
		addUser({
			name: newUser.full_name,
			mobile: newUser.mobile_number,
			email: newUser.email_id,
			organisationId: orgId,
			role: newUser.role,
			userId: loggedInUser?.user_id,
		})
			.then(() => {
				dispatch(showToast(true, strings('user_added_successfully'), { type: TOAST_TYPE.SUCCESS }))
				uiDispatch({ type: ACTIONS.RESET_NEW_USER })
				closeModal()
				fetchOrgUsers(orgId)
			})
			.catch((error) => {
				const errorMsg = error?.response?.data?.message ?? strings('msg_api_error')
				dispatch(showToast(true, errorMsg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => dispatch(showLoader(false)))
	}

	useEffect(() => {
		clearTimeout(searchTimeoutId.current)
		if (addressSearchQuery.length >= 4) {
			searchTimeoutId.current = setTimeout(() => {
				dispatch(showLoader(true))
				getAddressListFromBoth(addressSearchQuery)
					.then((response) => {
						uiDispatch({ type: ACTIONS.SET_ADDRESS_SEARCH_RESULTS, payload: response ?? [] })
						if (!response?.length) {
							dispatch(showToast(true, strings('no_address_found'), { type: TOAST_TYPE.ERROR }))
						} else {
							uiDispatch({ type: ACTIONS.SELECT_ADDRESS_FROM_SEARCH, payload: response[0] })
						}
					})
					.catch(() => dispatch(showToast(true, strings('no_address_found'), { type: TOAST_TYPE.ERROR })))
					.finally(() => dispatch(showLoader(false)))
			}, 2000)
		}
		return () => clearTimeout(searchTimeoutId.current)
	}, [addressSearchQuery])

	const onAddressConfirmed = (addressType, data) => {
		const orgId = org?.user_details?.organisation_id
		let request = {}

		switch (addressType) {
			case 'headoffice_address':
				request = { headoffice_address: data, address_type: addressType }
				break

			case 'communication_address':
				request = { communication_address: data, address_type: addressType }
				break

			case 'kyc_poc_details':
				request = { kyc_poc_details: new User(data).getUserDetails(), address_type: addressType }
				break

			default:
				break
		}

		const actionMap = {
			headoffice_address: ACTIONS.SET_HEADOFFICE_ADDRESS,
			communication_address: ACTIONS.SET_COMMUNICATION_ADDRESS,
			kyc_poc_details: ACTIONS.SET_KYC_POC_DETAILS,
		}

		dispatch(showLoader(true))
		updateOrganisationAddressApi(orgId, request)
			.then((response) => {
				uiDispatch({ type: actionMap[addressType], payload: response?.[addressType] })
				setOrgData((prev) => ({ ...prev, ...response }))
			})
			.catch((error) => {
				const errorMsg = error?.response?.data?.message ?? strings('msg_api_error')
				dispatch(showToast(true, errorMsg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => dispatch(showLoader(false)))
	}

	const submitNewAddress = () => {
		const orgId = org?.user_details?.organisation_id
		const addressPayload = new Address({ ...newAddress, complete_address: newAddress.street_address.trim() })
		const setList = activeModal === 'addAddress' ? ACTIONS.SET_ADDRESS_LIST : ACTIONS.SET_COMM_ADDRESS_LIST

		dispatch(showLoader(true))
		addAddressToOrganisation(addressPayload, orgId)
			.then(() => {
				dispatch(showToast(true, 'Address added successfully', { type: TOAST_TYPE.SUCCESS }))
				getUserAddressesApi(org?.user_details?.user_id)
					.then((response) => uiDispatch({ type: setList, payload: response ?? [] }))
					.catch(() => {})
				uiDispatch({ type: ACTIONS.SET_ADDRESS_MODAL_VIEW, payload: 'list' })
			})
			.catch((error) => {
				const errorMsg = error?.response?.data?.message ?? strings('msg_api_error')
				dispatch(showToast(true, errorMsg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => dispatch(showLoader(false)))
	}

	const footerHandler = () => {
		return (
			<BodySectionWrapper>
				<FooterWrapper>
					<SectionItemWrapper isStatement direction={'row'} marginTop={'0px'}>
						<ButtonIconWapper>
							<ButtonIcon
								type='secondary'
								small
								uppercase
								onClick={() => {
									preStep()
								}}
							>
								<ChevronLeftOutlined />
							</ButtonIcon>
							<ButtonIcon
								type='secondary'
								small
								margin={'0px 0px 0px 5px'}
								uppercase
								onClick={() => {
									nextStep()
								}}
							>
								<ChevronRightOutlined />
							</ButtonIcon>
						</ButtonIconWapper>
						<ActionButtonWapper>
							<Button
								xs
								type='secondary'
								variant='destructive'
								onClick={() => uiDispatch({ type: ACTIONS.SET_ACTIVE_REASON, payload: true })}
							>
								{strings('reject')}
							</Button>
							<Button
								xs
								type='primary'
								margin='0px 0px 0px 3px'
								onClick={() => {
									kycStatushandler('', selectedStep, true, 'POC Details request accept')
								}}
							>
								{strings('accept')}
							</Button>
						</ActionButtonWapper>
					</SectionItemWrapper>
					{isActiveReason && (
						<RejectReason
							kycStatushandler={kycStatushandler}
							setIsActiveReason={(value) => uiDispatch({ type: ACTIONS.SET_ACTIVE_REASON, payload: value })}
							setpNumber={selectedStep}
							stepName='POC Details'
							rejectReasonMsg={org?.kyc_reject_reason?.[2] || ''}
						/>
					)}
				</FooterWrapper>
			</BodySectionWrapper>
		)
	}

	return (
		<>
			<DrawerBodyWrapper>
				<SectionHeaderWrapper>
					<SectionHeader>{strings('poc_details')}</SectionHeader>
				</SectionHeaderWrapper>
				<SelectWithButtonWrapper>
					<Select
						id='select-poc-user'
						label={strings('user')}
						placeholder={strings('select', 'user')}
						data={userList ?? []}
						value={userList?.find((item) => item.user_id === selectedUser?.user_id) ?? {}}
						primaryKey='user_id'
						displayKey='display_name'
						onChange={(value) => uiDispatch({ type: ACTIONS.SET_SELECTED_USER, payload: value })}
						disabled={false}
					/>
					<IconWrapper
						xs
						type='secondary'
						disabled={!selectedUser || selectedUser?.user_id === (kycPocDetails?.user_id ?? org?.kyc_poc_details?.user_id)}
						onClick={() => onAddressConfirmed('kyc_poc_details', selectedUser)}
					>
						{kycPocDetails ?? org?.kyc_poc_details ? <Edit fontSize='small' /> : <Add fontSize='small' />}
					</IconWrapper>
				</SelectWithButtonWrapper>
				<AddNewUserWrapper>
					<Button xs type='secondary' onClick={() => openModal('addUser')}>
						{strings(['add', 'new', 'user'])}
					</Button>
				</AddNewUserWrapper>
				<Divider />
				<SectionHeaderWithActionWrapper>
					<SectionHeader>{strings('head_office_address')}</SectionHeader>
					<IconWrapper xs type='secondary' onClick={() => openModal('addAddress')}>
						{headofficeAddress ?? org?.headoffice_address ? <Edit fontSize='small' /> : <Add fontSize='small' />}
					</IconWrapper>
				</SectionHeaderWithActionWrapper>
				{(headofficeAddress ?? org?.headoffice_address) &&
					(() => {
						const addr = headofficeAddress ?? org.headoffice_address
						return (
							<AddressCard readOnly style={{ marginTop: Spacings.SPACING_2B }}>
								{addr.name_tag && <AddressCardLabel>{addr.name_tag}</AddressCardLabel>}
								<AddressCardLine marginBottom>{addr.complete_address}</AddressCardLine>
								<br />
								{addr.city && <AddressCardItem>{addr.city},&#160;</AddressCardItem>}
								{addr.state && <AddressCardItem>{addr.state}&#160;-&#160;</AddressCardItem>}
								{addr.pincode && <AddressCardItem>{addr.pincode}</AddressCardItem>}
							</AddressCard>
						)
					})()}
				<BasicModal
					isOpen={activeModal === 'addUser'}
					onClose={closeModal}
					width='40vw'
					minHeight='60vh'
					header={strings(['add', 'new', 'user'])}
				>
					<ModalContentWrapper>
						<ModalFormWrapper>
							<Input
								id='new-user-name'
								label={strings('full', 'name')}
								placeholder={strings(['enter', 'full', 'name'])}
								value={newUser.full_name}
								onChange={updateNewUser('full_name')}
							/>
							<MobileFieldWrapper>
								<Select
									id='new-user-country-code'
									data={CountryCodes}
									displayKey='dial_code'
									primaryKey='name'
									value={newUser.country_code}
									onChange={updateNewUser('country_code')}
									noDropIcon
									type='tel'
								/>
								<Input
									id='new-user-mobile'
									label={strings('mobile', 'number')}
									placeholder={strings(['enter', 'mobile', 'number'])}
									type='tel'
									pattern='^\d{7}\d+$'
									maxLength={10}
									value={newUser.mobile_number}
									onChange={updateNewUser('mobile_number')}
									errorMap={{ patternMismatch: `${strings('enter_valid_num')}` }}
								/>
							</MobileFieldWrapper>
							<Input
								id='new-user-email'
								label={strings('email', 'id')}
								placeholder={strings(['enter', 'email', 'id'])}
								type='email'
								value={newUser.email_id}
								onChange={updateNewUser('email_id')}
							/>
							<Select
								id='new-user-role'
								label={strings('role')}
								placeholder={strings('select', 'role')}
								data={orgUserRoleOptions}
								primaryKey='key'
								displayKey='label'
								value={orgUserRoleOptions.find((o) => o.key === newUser.role) ?? {}}
								onChange={(val) => updateNewUser('role')(val.key)}
							/>
						</ModalFormWrapper>
						<ModalFooterWrapper>
							<Button
								xs
								type='primary'
								onClick={submitNewUser}
								disabled={!newUser.full_name || !newUser.mobile_number || !newUser.role}
							>
								{strings('submit')}
							</Button>
						</ModalFooterWrapper>
					</ModalContentWrapper>
				</BasicModal>
				<BasicModal isOpen={activeModal === 'addAddress'} onClose={closeModal} width='40vw' minHeight='50vh'>
					<ModalContentWrapper>
						{addressModalView === 'list' ? (
							<>
								<AddressModalHeader>
									<SectionHeader>{strings('head_office_address')}</SectionHeader>
								</AddressModalHeader>
								<AddressListWrapper>
									{addressList.length === 0 ? (
										<NoAddressText>{strings('no_addresses_found')}</NoAddressText>
									) : (
										addressList.map((address) => (
											<AddressCard
												key={address.address_id ?? address.id}
												selected={selectedAddress?.address_id === address.address_id}
												onClick={() => uiDispatch({ type: ACTIONS.SET_SELECTED_ADDRESS, payload: address })}
											>
												{address.name_tag && <AddressCardLabel>{address.name_tag}</AddressCardLabel>}
												<AddressCardLine marginBottom>{address.complete_address}</AddressCardLine>
												<br />
												{address.city && <AddressCardItem>{address.city},&#160;</AddressCardItem>}
												{address.state && <AddressCardItem>{address.state}&#160;-&#160;</AddressCardItem>}
												{address.pincode && <AddressCardItem>{address.pincode}</AddressCardItem>}
											</AddressCard>
										))
									)}
								</AddressListWrapper>
								<AddressListFooterWrapper>
									<Button xs type='secondary' onClick={() => uiDispatch({ type: ACTIONS.SET_ADDRESS_MODAL_VIEW, payload: 'form' })}>
										{strings('add_address')}
									</Button>
									<Button
										xs
										type='primary'
										disabled={!selectedAddress}
										onClick={() => {
											onAddressConfirmed('headoffice_address', selectedAddress)
											closeModal()
										}}
									>
										{strings('confirm')}
									</Button>
								</AddressListFooterWrapper>
							</>
						) : (
							<>
								<ModalFormWrapper>
									<Input
										id='address-search'
										label={strings('search', 'address')}
										placeholder='Search by pincode / city / state / address'
										value={addressSearchQuery}
										onChange={(val) => uiDispatch({ type: ACTIONS.SET_ADDRESS_SEARCH_QUERY, payload: val })}
									/>
									{addressSearchResults.length > 0 && (
										<Select
											id='select-address-result'
											label={strings('select', 'address')}
											data={addressSearchResults}
											primaryKey='id'
											displayKey='complete_address'
											value={addressSearchResults.find((item) => item.id === newAddress.id) ?? {}}
											onChange={(val) => uiDispatch({ type: ACTIONS.SELECT_ADDRESS_FROM_SEARCH, payload: val })}
										/>
									)}
									<AddressFormRevealWrapper show={newAddress.city && newAddress.state}>
										<Input id='address-city' label={strings('city')} value={newAddress.city} disabled />
										<Input id='address-state' label={strings('state')} value={newAddress.state} disabled />
										<Input id='address-pincode' label={strings('pincode')} value={newAddress.pincode} disabled />
										<Input
											id='address-street'
											label={strings('complete', 'address')}
											placeholder={strings(['enter', 'complete', 'address'])}
											value={newAddress.street_address}
											onChange={(val) =>
												uiDispatch({ type: ACTIONS.UPDATE_NEW_ADDRESS, payload: { field: 'street_address', value: val } })
											}
										/>
									</AddressFormRevealWrapper>
								</ModalFormWrapper>
								<AddressListFooterWrapper>
									<Button xs type='secondary' onClick={() => uiDispatch({ type: ACTIONS.SET_ADDRESS_MODAL_VIEW, payload: 'list' })}>
										{strings('back')}
									</Button>
									<Button xs type='primary' disabled={!newAddress.street_address || !newAddress.city} onClick={submitNewAddress}>
										{strings('submit')}
									</Button>
								</AddressListFooterWrapper>
							</>
						)}
					</ModalContentWrapper>
				</BasicModal>
				<Divider />
				<SectionHeaderWithActionWrapper>
					<SectionHeader>{strings('communication_address')}</SectionHeader>
					<IconWrapper xs type='secondary' onClick={() => openModal('addCommunicationAddress')}>
						{communicationAddress ?? org?.communication_address ? <Edit fontSize='small' /> : <Add fontSize='small' />}
					</IconWrapper>
				</SectionHeaderWithActionWrapper>
				{(communicationAddress ?? org?.communication_address) &&
					(() => {
						const addr = communicationAddress ?? org.communication_address
						return (
							<AddressCard readOnly style={{ marginTop: Spacings.SPACING_2B }}>
								{addr.name_tag && <AddressCardLabel>{addr.name_tag}</AddressCardLabel>}
								<AddressCardLine marginBottom>{addr.complete_address}</AddressCardLine>
								<br />
								{addr.city && <AddressCardItem>{addr.city},&#160;</AddressCardItem>}
								{addr.state && <AddressCardItem>{addr.state}&#160;-&#160;</AddressCardItem>}
								{addr.pincode && <AddressCardItem>{addr.pincode}</AddressCardItem>}
							</AddressCard>
						)
					})()}
				<BasicModal isOpen={activeModal === 'addCommunicationAddress'} onClose={closeModal} width='40vw' minHeight='50vh'>
					<ModalContentWrapper>
						{addressModalView === 'list' ? (
							<>
								<AddressModalHeader>
									<SectionHeader>{strings('communication_address')}</SectionHeader>
								</AddressModalHeader>
								<AddressListWrapper>
									{commAddressList.length === 0 ? (
										<NoAddressText>{strings('no_addresses_found')}</NoAddressText>
									) : (
										commAddressList.map((address) => (
											<AddressCard
												key={address.address_id ?? address.id}
												selected={selectedCommAddress?.address_id === address.address_id}
												onClick={() => uiDispatch({ type: ACTIONS.SET_SELECTED_COMM_ADDRESS, payload: address })}
											>
												{address.name_tag && <AddressCardLabel>{address.name_tag}</AddressCardLabel>}
												<AddressCardLine marginBottom>{address.complete_address}</AddressCardLine>
												<br />
												{address.city && <AddressCardItem>{address.city},&#160;</AddressCardItem>}
												{address.state && <AddressCardItem>{address.state}&#160;-&#160;</AddressCardItem>}
												{address.pincode && <AddressCardItem>{address.pincode}</AddressCardItem>}
											</AddressCard>
										))
									)}
								</AddressListWrapper>
								<AddressListFooterWrapper>
									<Button xs type='secondary' onClick={() => uiDispatch({ type: ACTIONS.SET_ADDRESS_MODAL_VIEW, payload: 'form' })}>
										{strings('add_address')}
									</Button>
									<Button
										xs
										type='primary'
										disabled={!selectedCommAddress}
										onClick={() => {
											onAddressConfirmed('communication_address', selectedCommAddress)
											closeModal()
										}}
									>
										{strings('confirm')}
									</Button>
								</AddressListFooterWrapper>
							</>
						) : (
							<>
								<ModalFormWrapper>
									<Input
										id='comm-address-search'
										label={strings('search', 'address')}
										placeholder='Search by pincode / city / state / address'
										value={addressSearchQuery}
										onChange={(val) => uiDispatch({ type: ACTIONS.SET_ADDRESS_SEARCH_QUERY, payload: val })}
									/>
									{addressSearchResults.length > 0 && (
										<Select
											id='comm-select-address-result'
											label={strings('select', 'address')}
											data={addressSearchResults}
											primaryKey='id'
											displayKey='complete_address'
											value={addressSearchResults.find((item) => item.id === newAddress.id) ?? {}}
											onChange={(val) => uiDispatch({ type: ACTIONS.SELECT_ADDRESS_FROM_SEARCH, payload: val })}
										/>
									)}
									<AddressFormRevealWrapper show={newAddress.city && newAddress.state}>
										<Input id='comm-address-city' label={strings('city')} value={newAddress.city} disabled />
										<Input id='comm-address-state' label={strings('state')} value={newAddress.state} disabled />
										<Input id='comm-address-pincode' label={strings('pincode')} value={newAddress.pincode} disabled />
										<Input
											id='comm-address-street'
											label={strings('complete', 'address')}
											placeholder={strings(['enter', 'complete', 'address'])}
											value={newAddress.street_address}
											onChange={(val) =>
												uiDispatch({ type: ACTIONS.UPDATE_NEW_ADDRESS, payload: { field: 'street_address', value: val } })
											}
										/>
									</AddressFormRevealWrapper>
								</ModalFormWrapper>
								<AddressListFooterWrapper>
									<Button xs type='secondary' onClick={() => uiDispatch({ type: ACTIONS.SET_ADDRESS_MODAL_VIEW, payload: 'list' })}>
										{strings('back')}
									</Button>
									<Button xs type='primary' disabled={!newAddress.street_address || !newAddress.city} onClick={submitNewAddress}>
										{strings('submit')}
									</Button>
								</AddressListFooterWrapper>
							</>
						)}
					</ModalContentWrapper>
				</BasicModal>
			</DrawerBodyWrapper>
			{adminView && footerHandler()}
		</>
	)
}

export default PocDetails
